import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, ChevronLeft, DollarSign, Info, Loader2, Plus, Sparkles, TrendingUp, Unlock, Upload, X } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const DataContributionsPage = () => {
  const [stats, setStats] = useState(null);
  const [showContribute, setShowContribute] = useState(false);
  const [contributing, setContributing] = useState(false);
  const [contributed, setContributed] = useState(false);
  const [form, setForm] = useState({
    case_type: "personal_injury",
    jurisdiction: "",
    venue: "",
    outcome_type: "settlement",
    outcome_amount: 0,
    days_to_resolution: 0,
    insurance_carrier: "",
    liability_clarity: "clear",
    injury_severity: "moderate"
  });
  
  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/intelligence/contributions/stats/demo_firm`);
      setStats(res.data);
    } catch (e) {
      console.error(e);
    }
  };
  
  const submitContribution = async () => {
    setContributing(true);
    try {
      await axios.post(`${API}/intelligence/contributions/submit`, {
        contributor_id: "demo_firm",
        ...form
      });
      setContributed(true);
      setShowContribute(false);
      loadStats();
    } catch (e) {
      console.error(e);
    } finally {
      setContributing(false);
    }
  };
  
  useEffect(() => {
    loadStats();
  }, []);
  
  const tierColors = {
    bronze: "from-amber-600 to-orange-700",
    silver: "from-slate-400 to-slate-600",
    gold: "from-yellow-400 to-amber-500",
    platinum: "from-slate-300 to-slate-500"
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="data-contributions-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Data Flywheel</h1>
            <Badge className="bg-gradient-to-r from-orange-600 to-red-600">EARN CREDITS</Badge>
          </div>
          <p className="text-muted-foreground">Contribute anonymized case outcomes, earn credits, unlock premium features</p>
        </div>
        
        {contributed && (
          <Alert className="mb-6 bg-emerald-50 border-emerald-200">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800">
              Thank you for your contribution! You've earned credits that have been added to your balance.
            </AlertDescription>
          </Alert>
        )}
        
        {/* Stats Card */}
        {stats && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className={`inline-block px-4 py-2 rounded-full bg-gradient-to-r ${tierColors[stats.tier]} text-white font-bold mb-2`}>
                    {stats.tier.toUpperCase()}
                  </div>
                  <p className="text-sm text-muted-foreground">Current Tier</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">{stats.credits_balance}</p>
                  <p className="text-sm text-muted-foreground">Credits Balance</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">{stats.total_contributions}</p>
                  <p className="text-sm text-muted-foreground">Total Contributions</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-600">{stats.discount_percentage}%</p>
                  <p className="text-sm text-muted-foreground">Your Discount</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* How It Works */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>How the Data Flywheel Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                  <Upload className="h-6 w-6 text-blue-600" />
                </div>
                <h4 className="font-semibold mb-1">1. Contribute</h4>
                <p className="text-sm text-muted-foreground">Share anonymized case outcomes</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <DollarSign className="h-6 w-6 text-emerald-600" />
                </div>
                <h4 className="font-semibold mb-1">2. Earn Credits</h4>
                <p className="text-sm text-muted-foreground">Get 10-25 credits per outcome</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <h4 className="font-semibold mb-1">3. Level Up</h4>
                <p className="text-sm text-muted-foreground">Unlock higher tiers & discounts</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="h-6 w-6 text-amber-600" />
                </div>
                <h4 className="font-semibold mb-1">4. Better Intelligence</h4>
                <p className="text-sm text-muted-foreground">Everyone benefits from richer data</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Tier Benefits */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Tier Benefits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { tier: "bronze", contributions: "0+", discount: "0%", investigations: 1, priority: false },
                { tier: "silver", contributions: "20+", discount: "10%", investigations: 3, priority: false },
                { tier: "gold", contributions: "50+", discount: "15%", investigations: 5, priority: true },
                { tier: "platinum", contributions: "100+", discount: "25%", investigations: 10, priority: true }
              ].map((t, i) => (
                <div key={i} className={`p-4 rounded-lg border ${stats?.tier === t.tier ? 'ring-2 ring-blue-500' : ''}`}>
                  <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${tierColors[t.tier]} text-white text-sm font-bold mb-3`}>
                    {t.tier.toUpperCase()}
                  </div>
                  <ul className="text-sm space-y-2">
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> {t.contributions} contributions</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> {t.discount} discount</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> {t.investigations} free investigations/mo</li>
                    <li className="flex items-center gap-2">
                      {t.priority ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <X className="h-4 w-4 text-slate-300" />}
                      Priority support
                    </li>
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Button onClick={() => setShowContribute(true)} size="lg" className="w-full">
          <Plus className="h-5 w-5 mr-2" /> Contribute Case Outcome
        </Button>
        
        {/* Contribution Modal */}
        {showContribute && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Submit Case Outcome</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowContribute(false)}><X className="h-4 w-4" /></Button>
                </div>
                <CardDescription>All data is anonymized. No client names or identifying information.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Case Type *</Label>
                    <Select value={form.case_type} onValueChange={v => setForm({...form, case_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal_injury">Personal Injury</SelectItem>
                        <SelectItem value="employment">Employment</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="medical_malpractice">Medical Malpractice</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Jurisdiction *</Label>
                    <Input value={form.jurisdiction} onChange={e => setForm({...form, jurisdiction: e.target.value})} placeholder="e.g., California" />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Outcome Type *</Label>
                    <Select value={form.outcome_type} onValueChange={v => setForm({...form, outcome_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="settlement">Settlement</SelectItem>
                        <SelectItem value="verdict_plaintiff">Verdict - Plaintiff</SelectItem>
                        <SelectItem value="verdict_defendant">Verdict - Defendant</SelectItem>
                        <SelectItem value="dismissed">Dismissed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Outcome Amount ($)</Label>
                    <Input type="number" value={form.outcome_amount} onChange={e => setForm({...form, outcome_amount: parseFloat(e.target.value) || 0})} />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Insurance Carrier</Label>
                    <Input value={form.insurance_carrier} onChange={e => setForm({...form, insurance_carrier: e.target.value})} placeholder="e.g., State Farm" />
                  </div>
                  <div>
                    <Label>Days to Resolution</Label>
                    <Input type="number" value={form.days_to_resolution} onChange={e => setForm({...form, days_to_resolution: parseInt(e.target.value) || 0})} />
                  </div>
                </div>
                
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <strong>Earn more credits:</strong> Include insurance carrier (+3), outcome amount (+5), days to resolution (+2), and trial data (+5)
                  </AlertDescription>
                </Alert>
                
                <Button onClick={submitContribution} disabled={contributing || !form.jurisdiction} className="w-full">
                  {contributing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Submit Contribution
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default DataContributionsPage;
